import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { MoreVertical, Shield, ArrowRight, Pencil, Code2, Power, Trash2 } from 'lucide-react';
import { VSLProtection, COUNTRIES } from './types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { generateJsCode } from '@/utils/generateVideoEmbedCode';

interface Props {
  protection: VSLProtection;
  onEdit: (p: VSLProtection) => void;
  onToggle: (p: VSLProtection) => void;
  onDelete: (p: VSLProtection) => void;
}

export function ProtectionCard({ protection, onEdit, onToggle, onDelete }: Props) {
  useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();

  const handleCopyCode = async () => {
    const userId = user?.id || user?.id_user || user?.user_id;
    if (!userId || !protection.videoId) {
      toast({
        title: translateInterfaceText("Não foi possível gerar o código"),
        description: translateInterfaceText("A VSL protegida não foi encontrada."),
        variant: 'destructive',
      });
      return;
    }
    try {
      // Embed backend-first: data-video-origin="CLOAKER" faz o player resolver o
      // vídeo liberado no backend (o middleware decide quem vê o quê). O arquivo
      // da Bunny do vídeo protegido nunca é buscado — só o id_video vai na URL.
      const code = await generateJsCode({
        videoId: protection.videoId,
        userId,
        videoOrigin: 'CLOAKER',
      });
      await navigator.clipboard.writeText(code);
      toast({
        title: translateInterfaceText("Código copiado!"),
        description: translateInterfaceText("Cole o código na sua landing page para ativar a proteção."),
      });
    } catch {
      toast({ title: translateInterfaceText("Erro ao copiar"), description: translateInterfaceText("Tente novamente."), variant: 'destructive' });
    }
  };

  const getFlag = (countryCode: string) => {
    const code = countryCode.toUpperCase();
    return code.replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
  };

  const getPrimaryTitle = () => {
    if (protection.countries && protection.countries.length > 0) {
      const firstCountryCode = protection.countries[0];
      const country = COUNTRIES.find(c => c.code === firstCountryCode);
      const flag = getFlag(firstCountryCode);
      const extraCount = protection.countries.length - 1;

      const content = (
        <div className="flex items-center gap-2">
          <span className="text-[20px] leading-none">{flag}</span>
          <span className="truncate max-w-[180px]">{country?.name || firstCountryCode}</span>
          {extraCount > 0 && (
            <span className="text-[#64748B] font-medium text-[14px] whitespace-nowrap">
              +{extraCount}
            </span>
          )}
        </div>
      );

      if (protection.countries.length > 1) {
        const allNames = protection.countries
          .map(code => {
            const c = COUNTRIES.find(x => x.code === code);
            return `${getFlag(code)} ${c?.name || code}`;
          })
          .join(', ');

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-default">{content}</div>
              </TooltipTrigger>
              <TooltipContent className="bg-[#0F172A] text-white border-none rounded-lg p-2 text-[12px]">
                <p className="max-w-[200px]">{allNames}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }

      return content;
    }
    if (protection.regions && protection.regions.length > 0) {
      return <span className="truncate">{protection.regions[0]}</span>;
    }
    if (protection.utmRules && protection.utmRules.length > 0) {
      return <span className="truncate">{protection.utmRules[0].value}</span>;
    }
    return <span className="truncate">{protection.name || translateInterfaceText("Sem nome")}</span>;
  };

  const getRuleType = () => {
    if (protection.countries && protection.countries.length > 0) return translateInterfaceText("País");
    if (protection.regions && protection.regions.length > 0) return translateInterfaceText("Região");
    if (protection.utmRules && protection.utmRules.length > 0) return 'UTM';
    return 'Geral';
  };

  const formattedDate = new Date(protection.createdAt).toLocaleDateString(getInterfaceLanguage());

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow w-full max-w-[340px]">
      {/* Topo do card */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 px-3 py-1 bg-[#EFF6FF] rounded-full border border-[#DBEAFE]">
          <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
          <span className="text-[11px] font-semibold text-[#2563EB] uppercase tracking-wider">
            {translateInterfaceText("ATIVO")}</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 rounded-lg hover:bg-[#F3F4F6] transition-colors">
              <MoreVertical className="w-5 h-5 text-[#94A3B8]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-white border-[#EEF2F7] shadow-lg rounded-xl p-1">
            <DropdownMenuItem onClick={() => onEdit(protection)} className="rounded-lg cursor-pointer focus:bg-[#F1F5F9] transition-colors">
              <Pencil className="w-4 h-4 mr-2" /> {" "}{translateInterfaceText("Editar")}</DropdownMenuItem>
            <DropdownMenuItem onClick={handleCopyCode} className="rounded-lg cursor-pointer focus:bg-[#F1F5F9] transition-colors">
              <Code2 className="w-4 h-4 mr-2" /> {" "}{translateInterfaceText("Copiar código")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onToggle(protection)} className="rounded-lg cursor-pointer focus:bg-[#F1F5F9] transition-colors">
              <Power className="w-4 h-4 mr-2" /> {protection.enabled ? translateInterfaceText("Desativar") : translateInterfaceText("Ativar")}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#F3F4F6]" />
            <DropdownMenuItem onClick={() => onDelete(protection)} className="text-[#DC2626] rounded-lg cursor-pointer transition-colors data-[highlighted]:bg-[#DC2626] data-[highlighted]:text-white">
              <Trash2 className="w-4 h-4 mr-2" /> {" "}{translateInterfaceText("Excluir")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Título principal da regra */}
      <h3 className="text-[18px] font-bold text-[#0F172A] mb-4">
        {getPrimaryTitle()}
      </h3>

      {/* Bloco de estatística de acessos protegidos */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center">
          <Shield className="w-5 h-5 text-[#2563EB]" />
        </div>
        <div>
          <div className="text-[18px] font-bold text-[#0F172A] leading-tight">
            {protection.blockedToday ?? 0}
          </div>
          <div className="text-[12px] text-[#64748B]">{translateInterfaceText("acessos protegidos")}</div>
        </div>
      </div>

      {/* Informações principais da regra */}
      <div className="space-y-2.5 mb-6">
        <div className="flex justify-between items-center text-[13px]">
          <span className="text-[#64748B]">{translateInterfaceText("Tipo de regra:")}</span>
          <span className="font-medium text-[#0F172A]">{getRuleType()}</span>
        </div>
        <div className="flex justify-between items-center text-[13px]">
          <span className="text-[#64748B]">{translateInterfaceText("Ação aplicada:")}</span>
          <span className="font-semibold text-[#2563EB]">{translateInterfaceText("Bloquear acesso")}</span>
        </div>
        <div className="flex justify-between items-center text-[13px]">
          <span className="text-[#64748B]">{translateInterfaceText("Criado em:")}</span>
          <span className="font-bold text-[#0F172A]">{formattedDate}</span>
        </div>
      </div>

      {/* Linha divisória */}
      <div className="h-[1px] bg-[#F1F5F9] w-full mb-6" />

      {/* Bloco de mídia — VSL original x alternativa */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        {/* VSL Original */}
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-[11px] font-medium text-[#64748B] text-center">{translateInterfaceText("VSL Original")}</span>
          <div className="aspect-video bg-[#F1F5F9] rounded-lg overflow-hidden border border-[#E2E8F0]">
            {protection.videoThumb ? (
              <img src={protection.videoThumb} alt={translateInterfaceText("VSL Original")} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#CBD5E1]">
                <Shield className="w-6 h-6 opacity-20" />
              </div>
            )}
          </div>
        </div>

        {/* Centro com seta */}
        <div className="flex flex-col items-center gap-1 mt-6">
          <div className="w-7 h-7 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] flex items-center justify-center">
            <ArrowRight className="w-4 h-4 text-[#2563EB]" />
          </div>
          <span className="text-[9px] text-[#94A3B8] font-medium whitespace-nowrap">{translateInterfaceText("Quando acessar")}</span>
        </div>

        {/* VSL Alternativa */}
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-[11px] font-medium text-[#64748B] text-center">{translateInterfaceText("VSL Alternativa")}</span>
          <div className="aspect-video bg-[#F1F5F9] rounded-lg overflow-hidden border border-[#E2E8F0]">
            {protection.redirectVideoThumb ? (
              <img src={protection.redirectVideoThumb} alt={translateInterfaceText("VSL Alternativa")} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-white/50 text-[#CBD5E1] border-2 border-dashed border-[#E2E8F0]">
                <span className="text-[10px] text-center px-1">{translateInterfaceText("Nenhum vídeo")}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
